<template>
  <div class="vz-order-card vz-order-members-card">
    <div v-if="disabled" class="disabled-card"/>
    <div v-if="titleCard || useCountry" class="text-h1 color-dark mb-20 flex flex-space-between">
      <span>{{ titleCard }}</span>
      <vz-select
        v-if="useCountry"
        v-model="memberCountry"
        class="tiny-arrow"
        list-class="vz-order-members-card-country-list"
        :items="phoneCountries"
        :disabled="disabled || lockInternationalMember"
      />
    </div>
    <vz-switcher
      v-model="type"
      big
      :disabled="disabled || lockInternationalMember"
      :items="contractorEntityList"
    />
    <vz-order-member-corporation
      ref="memberCorporation"
      v-if="type === 'corporation'"
      v-model="corporationMemberForm"
      :disabled="disabled"
      :part-disabled="lockInternationalMember"
      :direction="direction"
      :use-email="useEmail"
      :is-order-page="isOrderPage"
    />
    <vz-order-member-individual
      v-else
      :direction="direction"
      :disabled="disabled"
      :part-disabled="lockInternationalMember"
      :use-email="useEmail"
      :is-order-page="isOrderPage"
    />
    <vz-checkbox
      v-if="useSendCode"
      big
      class="mt-10"
      :label="useCodeTitle"
      :modelValue="sendCode"
      :disabled="disabled || lockUserTypeConsignee"
      @change="onChangeSendCode"
    />
  </div>
</template>

<script setup lang="ts">
import {PropType} from "vue"
import VzOrderMemberIndividual from '@/components/personal/order/members/vz-order-member-individual.vue'
import VzOrderMemberCorporation from '@/components/personal/order/members/vz-order-member-corporation.vue'
import {useOrderStore} from '@/stores/order'
import {useLayoutStore} from '@/stores/personal/layout'
import {Country, DirectionName, OrderCustomer, SwitchItem} from '@/types/types'
import {storeToRefs} from "pinia"
import {phoneCountries} from "@/common/objects"

const props = defineProps({
  titleCard: {
    type: String,
    default: ''
  },
  useSendCode: {
    type: Boolean,
    default: false
  },
  useEmail: {
    type: Boolean,
    default: true
  },
  useCountry: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  direction: {
    type: String as PropType<DirectionName>,
    default: 'dispatch',
    validator: (val: string) => {
      if (['dispatch', 'destination', 'third'].includes(val)) {
        return true
      }
      console.error('VzOrderMembers prop DIRECTION must be one of this DISPATCH, DESTINATION or THIRD values')
      return false
    }
  },
  isOrderPage: {
    type: Boolean,
    default: true
  }
})

const {$confirm} = useNuxtApp()
const {orderForm} = storeToRefs(useOrderStore())
const {isEditOrder} = storeToRefs(useLayoutStore())
const memberCorporation = ref();
const isDispatch = computed((): boolean => {
  return props.direction === 'dispatch'
})


const useCodeTitle = computed((): string => {
  return type.value === 'individual' ? 'Отправить код получения' : 'Получить груз по коду или доверенности'
})

const type = computed({
  get() {
    return orderForm.value.customer[props.direction].type
  },
  async set(value: "individual" | "corporation") {
    if (type.value === 'corporation') {
      memberCorporation.value.clearProjectShowToast()
    }

    orderForm.value.customer[props.direction].type = value
    await pasteData()
  }
})
const memberCountry = computed({
  get(): Country {
    return orderForm.value.customer[props.direction].country
  },
  set(value: Country) {
    orderForm.value.customer[props.direction].country = value
  }
})

const sendCode = computed(() => {
  return orderForm.value.customer[props.direction].sendCode
})

const individualMemberForm = computed((): OrderCustomer['individual'] => {
  return orderForm.value.customer[props.direction].individual
})

const corporationMemberForm = computed((): OrderCustomer['corporation'] => {
  return orderForm.value.customer[props.direction].corporation
})

// Для международной перевозки с принятым грузом при редактировании запрещаем
// изменение Получателя, но разрешаем изменение его Телефона, Почты, Кода.
const lockInternationalMember = computed(() => {
  return isEditOrder.value &&
    !isDispatch.value &&
    !!orderForm.value.meta?.hardLockedInternationalContractor &&
    !!orderForm.value.meta?.taken
})

// Блокируем изменения по отправке кода получения
const lockUserTypeConsignee = computed(() => {
  return isEditOrder.value &&
    orderForm.value.meta?.userType === 'consignee'
})

const contractorEntityList = ref<SwitchItem[]>([
  {
    title: 'Физическое лицо',
    value: 'individual'
  },
  {
    title: 'Юридическое лицо',
    value: 'corporation'
  }
])

async function pasteData() {
  const shortCorp = orderForm.value.customer[props.direction].corporation
  const shortInd = orderForm.value.customer[props.direction].individual

  if (
    type.value === 'individual' &&
    (!shortCorp.phone.length || ['7', '375', '374'].includes(shortCorp.phone.toString()) || (shortCorp.phone.length && shortCorp.phone.toString() === shortInd.phone.toString())) &&
    (!shortCorp.email || (shortCorp.email && shortCorp.email === shortInd.email)) &&
    (!shortCorp.name || (shortCorp.name && shortCorp.name === shortInd.name.value))
  ) {
    return
  }

  if(
    type.value === 'corporation' &&
    (!shortInd.phone.length || ['7', '375', '374'].includes(shortInd.phone.toString()) || (shortInd.phone.length && shortInd.phone.toString() === shortCorp.phone.toString())) &&
    (!shortInd.email || (shortInd.email && shortInd.email === shortCorp.email)) &&
    (!shortInd.name.value || (shortInd.name && shortInd.name.value === shortCorp.name))
  ) {
    return
  }

  const res = await $confirm({
    buttonText: 'Перенести',
    cancelText: 'Пропустить',
    title: 'Перенос данных',
    text: 'У вас есть заполненные данные, хотите перенести ФИО/Телефон/Email в другую форму?'
  })

  if (!res) {
    return
  }

  if (type.value === 'corporation') {
    shortCorp.phone = shortInd.phone
    shortCorp.email = shortInd.email
    shortCorp.name = shortInd.name.value
    return
  }

  shortInd.phone = shortCorp.phone
  shortInd.email = shortCorp.email
  shortInd.name.value = shortCorp.name
}

async function onChangeSendCode(sendCode: boolean): Promise<void> {
  orderForm.value.customer[props.direction].sendCode = sendCode
  const anotherCode = isDispatch.value ? orderForm.value.customer.dispatch.sendCode : orderForm.value.customer.destination.sendCode

  if (!sendCode && !anotherCode) {
    const revertDirection = isDispatch.value ? 'destination' : 'dispatch'
    orderForm.value.customer[revertDirection].sendCode = true
  }
}
</script>
